import React from 'react';
import DiaryFeed from '../components/DiaryFeed';
import EnergyCenter from '../components/EnergyCenter';
import ExamCenter from '../components/ExamCenter';
import FitnessCenter from '../components/FitnessCenter';
import GamificationDashboard from '../components/GamificationDashboard';
import HealthManager from '../components/HealthManager';
import IntellectCenter from '../components/IntellectCenter';
import LifeGoalsCenter from '../components/LifeGoalsCenter';
import MBTITest from '../components/MBTITest';
import MindCenter from '../components/MindCenter';
import MissionCenter from '../components/MissionCenter';
import MyDashboard from '../components/MyDashboard';
import NutritionManager from '../components/NutritionManager';
import PanSpiritWeb from '../components/PanSpiritWeb';
import PathOfAscension from '../components/PathOfAscension';
import PeopleList from '../components/PeopleList';
import PersonDetail from '../components/PersonDetail';
import Profile from '../components/Profile';
import Settings from '../components/Settings';
import UserManagement from '../components/UserManagement';
import WellnessCenter from '../components/WellnessCenter';
import WebHome from '../platforms/web/WebHome';
import { dimensions } from '../platforms/web/desktop/dimensions';
import { useApplication, type ApplicationState } from './ApplicationState';

export function renderStandaloneRoute(state: ApplicationState): React.ReactNode {
  const {
    navigation,
    viewMode,
    showProfile,
    showUserMgmt,
    setViewMode,
    setShowProfile,
    setShowUserMgmt,
    nutritionEntryIntent,
    nutritionInitialDate,
    examLaunch,
    healthInitialTab,
    closeBodyWorkspaceChild,
    navigateWellnessWorkspace,
    openDiaryCompose,
    openMissionAction,
    openExam,
    fitnessSummary,
    setFitnessSummary,
    acceptMissionUpdate,
    loadGameStats,
    handleConfigChange,
    openNutrition,
    openHealth,
  } = state;
  if (showUserMgmt) return <UserManagement onClose={() => setShowUserMgmt(false)} />;
  if (showProfile) return <Profile onClose={() => setShowProfile(false)} />;
  if (viewMode === 'settings')
    return <Settings onClose={navigation.back} onConfigChange={handleConfigChange} />;
  if (viewMode === 'mbti') return <MBTITest onClose={navigation.back} />;
  if (viewMode === 'fitness')
    return (
      <FitnessCenter
        initialAction={navigation.current.params?.record ? 'record' : undefined}
        onClose={() => {
          closeBodyWorkspaceChild();
          loadGameStats();
        }}
        onNavigateWorkspace={navigateWellnessWorkspace}
      />
    );
  if (viewMode === 'nutrition')
    return (
      <NutritionManager
        onClose={() => {
          closeBodyWorkspaceChild();
          loadGameStats();
        }}
        onNavigateWorkspace={navigateWellnessWorkspace}
        onHpUpdated={(hp) =>
          setFitnessSummary((current: any) =>
            current ? { ...current, hp: hp.details } : { hp: hp.details },
          )
        }
        initialAction={
          nutritionEntryIntent === 'meal'
            ? 'add_meal'
            : nutritionEntryIntent === 'water'
              ? 'focus_water'
              : nutritionEntryIntent === 'review'
                ? 'review'
                : undefined
        }
        initialDate={nutritionInitialDate}
      />
    );
  if (viewMode === 'energy')
    return (
      <EnergyCenter
        onClose={() => {
          closeBodyWorkspaceChild();
          loadGameStats();
        }}
      />
    );
  if (viewMode === 'health')
    return (
      <HealthManager
        initialTab={healthInitialTab}
        onClose={closeBodyWorkspaceChild}
        onNavigateWorkspace={navigateWellnessWorkspace}
      />
    );
  if (viewMode === 'missions')
    return (
      <MissionCenter
        initialTaskId={navigation.current.params?.taskId as string | undefined}
        initialDimension={dimensions.find(d => d.key === navigation.current.params?.dimension)?.key}
        onClose={() => {
          navigation.back();
          loadGameStats();
        }}
        onMissionUpdated={acceptMissionUpdate}
        onOpenAction={openMissionAction}
      />
    );
  if (viewMode === 'wellness')
    return (
      <WellnessCenter
        onClose={() => {
          navigation.back();
          loadGameStats();
        }}
        onOpenHealth={(tab = 'overview') => openHealth(tab)}
        onOpenNutrition={() => openNutrition()}
        onOpenMissions={() => setViewMode('missions')}
        onOpenFitness={() => navigation.navigate('fitness')}
      />
    );
  if (viewMode === 'mind')
    return (
      <MindCenter
        onClose={() => {
          navigation.back();
          loadGameStats();
        }}
        onOpenEnergy={() => setViewMode('energy')}
        onComposeDiary={(type) => openDiaryCompose(type)}
        onStatsUpdated={loadGameStats}
        energyEp={
          Number.isFinite(Number(fitnessSummary?.ep?.ep))
            ? Math.max(0, Math.min(100, Math.round(Number(fitnessSummary?.ep?.ep))))
            : null
        }
        energyEpData={fitnessSummary?.ep}
      />
    );
  if (viewMode === 'intellect')
    return (
      <IntellectCenter
        onClose={navigation.back}
        onOpenExam={openExam}
        onOpenMBTI={() => setViewMode('mbti')}
        onOpenMissions={() => setViewMode('missions')}
        onComposeNote={() => openDiaryCompose('note')}
      />
    );
  if (viewMode === 'exam')
    return (
      <ExamCenter
        onClose={navigation.back}
        initialCategory={examLaunch.category || 'mixed'}
        initialPathwayId={examLaunch.pathwayId || ''}
        autoStart
        onOpenMBTI={() => setViewMode('mbti')}
        onOpenMissions={() => setViewMode('missions')}
      />
    );
  if (viewMode === 'goals') return <LifeGoalsCenter initialGoalId={Number(navigation.current.params?.goalId) || undefined} onClose={navigation.back} />;

  return null;
}

export function WorkspaceRoute() {
  const {
    authUser,
    navigation,
    viewMode,
    selectedPersonId,
    setViewMode,
    recordsInitialFilter,
    diaryComposeIntent,
    openDiaryCompose,
    people,
    dimensionScores,
    weeklyMission, missionLoading, missionError, openMissionAction,
    loadGameStats,
    loadPeople,
    healthHp,
    mindEp,
    refreshGameStatsThrottled,
    handlePersonSelect,
    handleBackToList,
    handlePersonCreated,
    handlePersonDeleted,
    openNutrition,
    openRecords,
    consumeDiaryCompose,
  } = useApplication();
  return selectedPersonId ? (
    <PersonDetail
      personId={selectedPersonId}
      onBack={handleBackToList}
      onPersonDeleted={handlePersonDeleted}
    />
  ) : (
    <>
      {viewMode === 'diary' && (
        <WebHome
          displayName={authUser?.nickname || authUser?.username}
          hp={healthHp}
          ep={mindEp}
          dimensionScores={dimensionScores}
          mission={weeklyMission} missionLoading={missionLoading} missionError={missionError}
          onRetry={loadGameStats} onOpenAction={openMissionAction}
          onOpenTask={id => navigation.navigate('missions', {taskId: String(id)})}
          onRecord={() => openDiaryCompose('categories')}
          onQuickRecord={openDiaryCompose}
          onOpenTasks={() => setViewMode('missions')}
          onOpenRecords={() => openRecords('all')}
          onOpenDimension={(key) => {
            const dimension = dimensions.find((d) => d.key === key);
            if (dimension) navigation.navigate(dimension.route);
          }}
        />
      )}
      {viewMode === 'records' && (
        <DiaryFeed
          initialFilterType={recordsInitialFilter}
          initialRecordKey={navigation.current.params?.recordKey as string | undefined}
          onPersonClick={handlePersonSelect}
          onDiarySaved={() => {
            loadGameStats();
          }}
          onNavigateExercise={() => navigation.navigate('fitness')}
          onNavigateNutrition={(mode, date) => {
            openNutrition(mode, date);
          }}
          composeIntent={diaryComposeIntent}
          onComposeIntentHandled={consumeDiaryCompose}
        />
      )}
      {viewMode === 'people' && (
        <PeopleList
          people={people}
          onPersonSelect={handlePersonSelect}
          onPersonCreated={handlePersonCreated}
          onRefresh={loadPeople}
          onBackHome={() => {
            navigation.switchTab('diary');
            refreshGameStatsThrottled();
          }}
        />
      )}
      {viewMode === 'gamification' && <GamificationDashboard onClose={navigation.back} />}
      {viewMode === 'pathway' && (
        <PathOfAscension
          initialPathwayId={
            typeof navigation.current.params?.pathwayId === 'string'
              ? navigation.current.params.pathwayId
              : undefined
          }
          onClose={() => {
            navigation.back();
            loadGameStats();
          }}
        />
      )}
      {viewMode === 'pan-spirit' && (
        <PanSpiritWeb
          onClose={navigation.back}
          onPublish={() => openDiaryCompose('public-note')}
          onOpenPeople={() => navigation.navigate('people')}
        />
      )}
      {viewMode === 'mine' && <MyDashboard onOpenMBTI={() => setViewMode('mbti')} />}
    </>
  );
}
